import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Holds an in-progress B2B order being built on the OrderSheet page,
// then handed to the OrderReview page. Persisted to sessionStorage so a
// refresh mid-flow doesn't blow it away.
//
// Shape:
//   draft = {
//     [variantSku]: {
//       sku, itemId, itemName, colorId, colorName, size, warehouse,
//       qty, unitPrice, currency
//     }
//   }
// One row per (item × color × size × warehouse) so the same color can
// be ordered against US and INTL warehouses with their own prices.

const KEY = "mr.b2b.orderDraft";
const OrderDraftContext = createContext(null);

function load() {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function OrderDraftProvider({ children }) {
  const [draft, setDraft] = useState(load);

  useEffect(() => {
    try { sessionStorage.setItem(KEY, JSON.stringify(draft)); } catch { /* quota */ }
  }, [draft]);

  const setLine = useCallback((key, line) => {
    setDraft((d) => {
      const next = { ...d };
      if (!line || !line.qty || line.qty <= 0) {
        delete next[key];
      } else {
        next[key] = line;
      }
      return next;
    });
  }, []);

  const setQty = useCallback((key, qty, fallbackLine) => {
    setDraft((d) => {
      const next = { ...d };
      const prev = d[key] || fallbackLine;
      if (!prev) return d;
      if (!qty || qty <= 0) {
        delete next[key];
      } else {
        next[key] = { ...prev, qty };
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setDraft({}), []);

  const lines = useMemo(() => Object.entries(draft).map(([k, v]) => ({ key: k, ...v })), [draft]);
  const totalQty = useMemo(() => lines.reduce((n, l) => n + (l.qty || 0), 0), [lines]);
  const totalAmount = useMemo(
    () => lines.reduce((n, l) => n + (Number(l.unitPrice) || 0) * (l.qty || 0), 0),
    [lines]
  );

  const value = useMemo(
    () => ({ draft, lines, totalQty, totalAmount, setLine, setQty, clear }),
    [draft, lines, totalQty, totalAmount, setLine, setQty, clear]
  );

  return <OrderDraftContext.Provider value={value}>{children}</OrderDraftContext.Provider>;
}

export function useOrderDraft() {
  const ctx = useContext(OrderDraftContext);
  if (!ctx) throw new Error("useOrderDraft must be used within OrderDraftProvider");
  return ctx;
}

export function lineKey({ sku, warehouse }) {
  return `${sku}::${warehouse}`;
}
